import { createContext, useContext, useEffect, useState } from 'react';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { kebabCase } from 'lodash';
import type { RootState, AppDispatch } from './store';
import { Parameter, globalParameters } from './parameters';
import { globalParameterSelector } from '../stores/globalParameters';

export { useWidgetsTheme } from './theme';
export { useTranslate } from '@src/i18n/useTranslate';

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export interface ParameterInfo {
  parameters: Parameter[];
  values: { [key: string]: string | undefined };
}

export const WidgetParamContext = createContext<ParameterInfo>({
  parameters: [],
  values: {},
});

export const useWidgetLocalParams = () => useContext(WidgetParamContext);

export const useWidgetParam = (key: string): string | undefined => {
  key = kebabCase(key);
  const globalValue = useAppSelector(globalParameterSelector(key));
  const ctxtParams = useContext(WidgetParamContext);

  if (ctxtParams) {
    const widgetParam = ctxtParams.parameters.find((p) => p.name === key);
    if (widgetParam) {
      return ctxtParams.values[key] ?? globalValue ?? paramDefaultValue(widgetParam);
    }
  }

  const globalParam = globalParameters.find((p) => p.name === key);
  if (globalParam) {
    return globalValue ?? paramDefaultValue(globalParam);
  }

  return undefined;
};

const paramDefaultValue = (param: Parameter) => (param.optional ? param.defaultValue ?? undefined : undefined);

export const useJSONLocalStorage = (key: string, initialValue?: unknown) => {
  const stringifiedInitialValue = JSON.stringify(initialValue ?? 'null');
  const { setItem: setItemLocal, getItem: getItemLocal, removeItem } = useLocalStorage(key, stringifiedInitialValue);

  const setItem = (value: unknown) => setItemLocal(JSON.stringify(value));

  const getItem = () => JSON.parse(getItemLocal() ?? stringifiedInitialValue);

  return { setItem, getItem, removeItem };
};

export const useLocalStorage = (key: string, initialValue?: string) => {
  const [value, setValue] = useState(() => {
    const storedValue = localStorage.getItem(key);
    return storedValue ? storedValue : initialValue;
  });

  const setItem = (newValue: string) => {
    setValue(newValue);
    localStorage.setItem(key, newValue);
  };

  const getItem = () => {
    return value;
  };

  const removeItem = () => {
    setValue(initialValue);
    localStorage.removeItem(key);
  };

  return { setItem, getItem, removeItem };
};

export const useBreakPoints = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  return {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1536,
    currentBreakpoint: width,
  };
};
